import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useBreakpoint } from '~/techstyle-shared/react-components';

import {
  GAP_BETWEEN_GRID_COLUMNS_DESKTOP,
  GAP_BETWEEN_GRID_COLUMNS_MOBILE,
  GAP_BETWEEN_GRID_ROWS,
  GRID_CARD_TYPES,
} from '../constants';
import useGridInputs from '../hooks/useGridInputs';
import useNumGridCols from '../hooks/useNumGridCols';
import { getCellHeight } from '../utils/measurements';

import AssetCard from './AssetCard';
import GridIndexInfo from './GridIndexInfo';
import ProductCard from './ProductCard/ProductCard';

const GridIndexInfoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  background-color: ${({ theme }) => theme.colors.palette.white};
  width: 80%;
`;

const StyledAssetCard = styled(AssetCard)`
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  display: block;
  overflow: hidden;
  border-radius: 12px;
`;

const GridCardRenderer = ({
  item,
  dynamicCardWidth,
  row,
  column,
  showIndexInfo = false, // TODO: make this a FF or debug admin option
}) => {
  const { isQuickViewEnabled } = useGridInputs();
  const numGridColumns = useNumGridCols();
  const columnSpan = item?.span?.columns || 1;
  const rowSpan = item?.span?.rows || 1;
  const { isMobile } = useBreakpoint();
  const gridIndex = row * numGridColumns + column;
  const hasDynamicWidth = typeof dynamicCardWidth === 'number';
  const cellHeight = hasDynamicWidth
    ? getCellHeight({ isMobile, dynamicCardWidth, isQuickViewEnabled })
    : null;

  return (
    <div
      style={{
        gridColumn: `span ${columnSpan}`,
        gridRow: `span ${rowSpan}`,
        width: hasDynamicWidth
          ? dynamicCardWidth * columnSpan +
            (isMobile
              ? GAP_BETWEEN_GRID_COLUMNS_MOBILE
              : GAP_BETWEEN_GRID_COLUMNS_DESKTOP) *
              (columnSpan - 1)
          : '100%',
        height: hasDynamicWidth
          ? cellHeight * rowSpan + GAP_BETWEEN_GRID_ROWS * (rowSpan - 1)
          : undefined,
        position: 'relative',
      }}
    >
      {showIndexInfo && (
        <GridIndexInfoWrapper>
          <GridIndexInfo
            numGridColumns={numGridColumns}
            row={row}
            column={column}
            customVars={item?.options?.customVars}
          />
        </GridIndexInfoWrapper>
      )}
      {item?.type === GRID_CARD_TYPES.ASSET ? (
        <StyledAssetCard {...item} />
      ) : (
        <ProductCard {...item} gridIndex={gridIndex} />
      )}
    </div>
  );
};

GridCardRenderer.propTypes = {
  column: PropTypes.number.isRequired,
  dynamicCardWidth: PropTypes.number,
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    span: PropTypes.shape({
      rows: PropTypes.number,
      columns: PropTypes.number,
    }),
    options: PropTypes.shape({
      customVars: PropTypes.object,
    }),
  }).isRequired,
  row: PropTypes.number.isRequired,
  showIndexInfo: PropTypes.bool,
  variant: PropTypes.string,
};

export default GridCardRenderer;
